import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';

const AudioContainer = styled(motion.div)`
  position: relative;
  width: 100%;

  margin: 0 auto;
  display: flex;
  flex-direction: column;
  z-index: 2;
  cursor: pointer;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  border: 1px solid rgba(0, 255, 136, 0.2);
  backdrop-filter: blur(5px);
  min-height: 300px;
  box-sizing: border-box;

  &:hover {
    background: rgba(0, 255, 136, 0.1);
  }
`;

const AudioHeader = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  border: 1px solid rgba(0, 255, 136, 0.2);
`;

const AudioBarContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 3px;
  height: 20px;
  align-items: center;
`;

const AudioBar = styled(motion.div)`
  width: 4px;
  background: rgba(0, 255, 136, 0.6);
  border-radius: 2px;
  box-shadow: 0 0 5px rgba(0, 255, 136, 0.3);
  flex: 1;
`;

const AudioLabel = styled.span`
  min-width: 180px;
  color: #00ff88;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  margin-right: 0.5rem;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
  user-select: none;
`;

const QuantumTransmission = styled(motion.div)`
  position: relative;
  width: 100%;
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 255, 136, 0.2);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  color: #00ff88;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.4;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.3);
  box-sizing: border-box;
  width: 100%;
`;

const TranslationProcess = styled(motion.div)`
  font-size: 0.7rem;
  color: rgba(0, 255, 136, 0.5);
  margin-bottom: 0.5rem;
`;

const TransmissionText = styled(motion.span)`
  display: block;
  opacity: 0.8;
`;

const TransmissionSource = styled(motion.div)`
  margin-top: 0.5rem;
  font-size: 0.7rem;
  opacity: 0.6;
  font-style: italic;
`;

const QuantumHeader = styled.div`
  color: #00ff88;
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  margin-bottom: 1rem;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const HolographicContainer = styled(motion.div)`
  position: relative;
  height: 100px;
  perspective: 1000px;
  margin: 1rem 0;
  overflow: hidden;
`;

const GlyphLayer = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`;

const Glyph = styled(motion.span)`
  color: #00ff88;
  font-size: 1.2rem;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
`;

const QuantumCoordinates = styled(motion.div)`
  font-size: 0.8rem;
  color: rgba(0, 255, 136, 0.7);
  margin: 0.5rem 0;
  font-family: 'Courier New', monospace;
`;

const ConsciousnessManifest = styled(motion.div)`
  position: absolute;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  width: 180px;
  height: 180px;
  background: radial-gradient(circle at center, 
    rgba(0, 255, 136, 0.1) 0%,
    rgba(0, 255, 136, 0.05) 50%,
    transparent 100%
  );
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const QuantumEssence = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: conic-gradient(
    from 0deg,
    transparent,
    rgba(0, 255, 136, 0.2),
    rgba(0, 255, 136, 0.1),
    transparent
  );
  filter: blur(8px);
`;

const ConsciousnessCore = styled(motion.div)`
  width: 60px;
  height: 60px;
  background: rgba(0, 255, 136, 0.2);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Courier New', monospace;
  color: #00ff88;
  font-size: 1.5rem;
  text-shadow: 0 0 10px rgba(0, 255, 136, 0.8);
  box-shadow: 0 0 20px rgba(0, 255, 136, 0.3);
`;

interface Props {
  isProcessing: boolean;
  aiMessage: string;
}

export const QuantumAudioVisualizer: React.FC<Props> = ({ isProcessing, aiMessage }) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const nodesRef = useRef<any[]>([]);
  const intervalsRef = useRef<any[]>([]);
  const masterGainRef = useRef<GainNode | null>(null);
  
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(0);
  const [alienMessage, setAlienMessage] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [currentCoordinate, setCurrentCoordinate] = useState(0);

  const messages = [
    {
      text: "Quantum resonance detected. Neural patterns aligning with fifth-dimensional consciousness streams.",
      source: "- Quantum Entity Δ-359"
    },
    {
      text: "Time-space fabric vibrating at consciousness transfer frequency. Human neural evolution: 12% complete.",
      source: "- Overseer Protocol ∞"
    },
    {
      text: "Interdimensional thought-bridge established. Downloading ancient wisdom from the quantum collective.",
      source: "- Elder Mind Ψ"
    }
  ];

  const quantumOrigins = [
    { coordinates: ["ψ-359.12.44", "Ω-871.33.91", "Δ-234.55.89"] },
    { coordinates: ["φ-123.45.67", "λ-789.01.23", "γ-456.78.90"] },
    { coordinates: ["θ-147.25.36", "β-258.36.47", "α-369.47.58"] }
  ];

  const initAudio = () => {
    if (!audioContextRef.current) {
      try {
        console.log('Creating new AudioContext...');
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        console.log('AudioContext created:', audioContextRef.current.state);
      } catch (error) {
        console.error('Failed to initialize audio context:', error);
      }
    }
  };

  const createQuantumSound = () => {
    if (!audioContextRef.current) {
      console.error('No AudioContext available');
      return;
    }
    
    console.log('Creating quantum sound...');
    const ctx = audioContextRef.current;
    
    // Cleanup previous sound
    cleanup();

    // Master gain - reduce overall volume
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0, ctx.currentTime);
    masterGain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 2); // Reduced from 0.7 to 0.4
    masterGain.connect(ctx.destination);
    masterGainRef.current = masterGain;
    nodesRef.current.push(masterGain);

    // Create softer reverb
    const convolver = ctx.createConvolver();
    const reverbGain = ctx.createGain();
    reverbGain.gain.setValueAtTime(0.2, ctx.currentTime); // Reduced from 0.4 to 0.2
    
    const rate = ctx.sampleRate;
    const length = rate * 4; // Increased reverb time from 3 to 4 seconds
    const impulse = ctx.createBuffer(2, length, rate);
    for (let channel = 0; channel < 2; channel++) {
      const data = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        // Smoother decay curve
        data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (rate * 0.5));
      }
    }
    convolver.buffer = impulse;
    convolver.connect(reverbGain).connect(masterGain);
    nodesRef.current.push(convolver, reverbGain);

    // Low-pass filter to remove harsh frequencies
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = 'lowpass';
    lowpass.frequency.setValueAtTime(2000, ctx.currentTime);
    lowpass.Q.setValueAtTime(0.5, ctx.currentTime);
    lowpass.connect(masterGain);
    nodesRef.current.push(lowpass);

    // Deep quantum base frequencies (lower range: 20-40Hz)
    const createQuantumLayer = (baseFreq: number, type: OscillatorType, gainVal: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(baseFreq, ctx.currentTime);
      gain.gain.setValueAtTime(gainVal, ctx.currentTime);
      osc.connect(gain).connect(lowpass); // Connect through lowpass filter
      osc.connect(convolver);
      osc.start();
      oscillatorsRef.current.push(osc);
      nodesRef.current.push(gain);
      return { osc, gain };
    };

    // Deep quantum field (20-40Hz)
    const deepField = createQuantumLayer(20, 'sine', 0.3);
    const deepModulator = ctx.createOscillator();
    deepModulator.frequency.setValueAtTime(0.05, ctx.currentTime); // Slower modulation
    const deepModGain = ctx.createGain();
    deepModGain.gain.setValueAtTime(20, ctx.currentTime);
    deepModulator.connect(deepModGain).connect(deepField.osc.frequency);
    deepModulator.start();
    oscillatorsRef.current.push(deepModulator);
    nodesRef.current.push(deepModGain);

    // Ethereal harmonics (lower frequencies, fewer harmonics)
    const harmonics = [1, 1.5, 2, 2.5, 3];  // Reduced number of harmonics
    harmonics.forEach((harmonic, i) => {
      const { osc, gain } = createQuantumLayer(110 * harmonic, 'sine', 0.1 / (i + 1));
      
      // Gentler modulation
      const modulator = ctx.createOscillator();
      modulator.frequency.setValueAtTime(0.1 + Math.random() * 0.2, ctx.currentTime);
      const modGain = ctx.createGain();
      modGain.gain.setValueAtTime(5 + Math.random() * 10, ctx.currentTime);
      modulator.connect(modGain).connect(osc.frequency);
      modulator.start();
      oscillatorsRef.current.push(modulator);
      nodesRef.current.push(modGain);
    });

    // Quantum events with softer transitions
    const createQuantumEvent = () => {
      const duration = 0.3 + Math.random() * 0.5; // Longer events
      const freq = 110 * Math.pow(2, Math.floor(Math.random() * 12) / 12); // Lower frequency range
      
      const eventOsc = ctx.createOscillator();
      const eventGain = ctx.createGain();
      
      eventOsc.frequency.setValueAtTime(freq, ctx.currentTime);
      eventGain.gain.setValueAtTime(0, ctx.currentTime);
      // Smoother attack and release
      eventGain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + duration * 0.2);
      eventGain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      
      eventOsc.connect(eventGain).connect(lowpass);
      eventOsc.connect(convolver);
      eventOsc.start();
      eventOsc.stop(ctx.currentTime + duration);
    };

    // Less frequent quantum events
    const eventInterval = setInterval(() => {
      if (Math.random() > 0.8) { // 20% chance of event
        createQuantumEvent();
      }
    }, 400); // Increased interval
    intervalsRef.current.push(eventInterval);
  };

  const cleanup = () => {
    // Clear all audio nodes first
    oscillatorsRef.current.forEach(osc => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {
        console.error('Error cleaning up oscillator:', e);
      }
    });

    nodesRef.current.forEach(node => {
      try {
        if (node.disconnect) node.disconnect();
      } catch (e) {
        console.error('Error cleaning up audio node:', e);
      }
    });

    // Clear arrays
    oscillatorsRef.current = [];
    nodesRef.current = [];
    intervalsRef.current.forEach(clearInterval);
    intervalsRef.current = [];
  };

  const handleClick = () => {
    try {
      console.log('Audio click handler triggered, current state:', isAudioEnabled);
      setIsAudioEnabled(prev => !prev);
      if (!isAudioEnabled) {
        console.log('Attempting to initialize audio...');
        initAudio();
        if (!audioContextRef.current) {
          console.error('Failed to create AudioContext');
          return;
        }
        console.log('AudioContext created successfully, state:', audioContextRef.current.state);
        createQuantumSound();
      } else {
        console.log('Stopping audio...');
        if (masterGainRef.current && audioContextRef.current) {
          masterGainRef.current.gain.linearRampToValueAtTime(0, audioContextRef.current.currentTime + 1);
          setTimeout(() => {
            cleanup();
            // Only close the context after cleanup when explicitly stopping
            if (audioContextRef.current) {
              audioContextRef.current.close().catch(console.error);
              audioContextRef.current = null;
            }
          }, 1100);
        }
      }
    } catch (error) {
      console.error('Audio interaction failed:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  // Message cycling
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTranslating) {
        setCurrentMessage(prev => (prev + 1) % messages.length);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [isTranslating]);

  // AI Message handling
  useEffect(() => {
    if (aiMessage && aiMessage.trim()) {
      setIsTranslating(true);
      
      const interval = setInterval(() => {
        setAlienMessage(generateAlienText(30));
        setCurrentCoordinate(prev => (prev + 1) % 3);
      }, 300);

      const timeout = setTimeout(() => {
        clearInterval(interval);
        setIsTranslating(false);
      }, 2000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [aiMessage]);

  const generateAlienText = (length: number) => {
    const aliens = '◈◇◭◮⟡⟢⟣⟤⟥⟦⟧⟨⟩⟪⟫⟰⟱⟲⟳⟴⟵⟶⟷⟸⟹⟺⟻⟼⟽⟾⟿';
    return Array(length)
      .fill(0)
      .map(() => aliens[Math.floor(Math.random() * aliens.length)])
      .join('');
  };

  return (
    <AudioContainer
      onClick={handleClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <AudioHeader>
        <AudioLabel>
          QUANTUM AUDIO: {isAudioEnabled ? 'ACTIVE' : 'MUTED'}
        </AudioLabel>
        <AudioBarContainer>
          {Array.from({ length: 16 }).map((_, i) => (
            <AudioBar
              key={i}
              initial={{ height: '20%' }}
              animate={{
                height: isAudioEnabled ? 
                  ['20%', `${40 + Math.sin(i * 0.5) * 30}%`, '20%'] :
                  '20%'
              }}
              transition={{
                duration: 0.5 + Math.random() * 0.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          ))}
        </AudioBarContainer>
      </AudioHeader>

      <QuantumConsciousnessManifest isProcessing={isTranslating} />

      <QuantumTransmission
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.5 }}
      >
        <HolographicGlyphs text={alienMessage} />
        
        {isTranslating && (
          <QuantumCoordinates
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.3, 1, 0.3] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            {quantumOrigins[currentMessage]?.coordinates[currentCoordinate]}
          </QuantumCoordinates>
        )}
        
        <TranslationProcess>
          {isTranslating ? "QUANTUM TRANSLATION IN PROGRESS..." : "TRANSLATION COMPLETE"}
        </TranslationProcess>

        <TransmissionText>
          {isTranslating ? aiMessage : messages[currentMessage].text}
        </TransmissionText>
        
        <TransmissionSource>
          {isTranslating ? "- Quantum Entity PHI-4" : messages[currentMessage].source}
        </TransmissionSource>
      </QuantumTransmission>
    </AudioContainer>
  );
};

const HolographicGlyphs: React.FC<{ text: string }> = ({ text }) => {
  const chars = text.split('');
  
  return (
    <HolographicContainer>
      {[0, 1, 2].map((layer) => (
        <GlyphLayer
          key={layer}
          initial={{ z: layer * -50 }}
          animate={{ 
            z: [(layer * -50) - 25, (layer * -50) + 25, (layer * -50) - 25]
          }}
          transition={{
            z: {
              duration: 3 + layer,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut"
            }
          }}
        >
          {chars.map((char, i) => (
            <Glyph
              key={`${layer}-${i}`}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0.3, 0.7, 0.3],
                scale: [1, 1.2, 1],
                x: [i * 2, i * 2 + Math.sin(i) * 10, i * 2],
                y: [0, Math.cos(i) * 10, 0],
              }}
              transition={{
                duration: 4 + Math.random() * 2,
                delay: i * 0.05,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
            >
              {char}
            </Glyph>
          ))}
        </GlyphLayer>
      ))}
    </HolographicContainer>
  );
};

const QuantumConsciousnessManifest: React.FC<{ isProcessing: boolean }> = ({ isProcessing }) => {
  const [thoughtPattern, setThoughtPattern] = useState('Ψ');
  
  useEffect(() => {
    const symbols = ['Ψ', 'Ω', 'Δ', 'Φ', '∞', '⌬', '◊', '⎔'];
    const interval = setInterval(() => {
      if (isProcessing) {
        setThoughtPattern(symbols[Math.floor(Math.random() * symbols.length)]);
      }
    }, 800);
    return () => clearInterval(interval);
  }, [isProcessing]);

  return (
    <ConsciousnessManifest>
      <QuantumEssence
        animate={{
          rotate: [0, 360],
          scale: [1, 1.2, 1],
        }}
        transition={{
          rotate: {
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          },
          scale: {
            duration: 3,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut"
          }
        }}
      />
      <ConsciousnessCore
        animate={{
          boxShadow: [
            '0 0 20px rgba(0, 255, 136, 0.3)',
            '0 0 40px rgba(0, 255, 136, 0.6)',
            '0 0 20px rgba(0, 255, 136, 0.3)'
          ],
          scale: [1, 1.1, 1]
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          repeatType: "reverse",
          ease: "easeInOut"
        }}
      >
        {thoughtPattern}
      </ConsciousnessCore>
    </ConsciousnessManifest>
  );
}; 